<template>
  <section class="h-full mx-4">
    <BaseTable
      v-if="routeDefinitions"
      :headers="routeDefinitionHeaders"
      :list="routeDefinitions"
      @add-new="onAddNewRouteDefinition"
      @deleteMultiselect="onDeleteMultiselect"
      @refresh-data="loadData"
      table-id="route-definitions"
      clickable
      @table-item-clicked="onEditRouteDefinition"
      @apply-filters="onApplyFilters"
      @reset-filters="onResetFilters">
      <template #actions="{ item }: any">
        <div class="flex items-center justify-center gap-1">
          <ActionButton
            :isEnabled="item.isFromManager"
            color="red"
            :icon="IconTrashOutline"
            @action="onDeleteRow(item as RouteDefinitions)" />
        </div>
      </template>
      <template #filters>
        <section class="flex flex-col items-start justify-start w-full max-w-xl py-4 mx-auto">
          <div>
            <label class="space-y-2 text-sm tracking-wide text-gray-400 uppercase">{{
              $t('baseObject.isActive')
            }}</label>
            <BaseToggle :label="$t('material.active')" name="isActive" v-model="filterIsActive" />
          </div>
        </section>
      </template>
    </BaseTable>

    <RouteDefinitionModalCreateEdit
      v-if="createEditModalActive"
      :route-definition="routeDefinition"
      :mode="createEditModalMode"
      :title="modalEditCreateTitle()"
      @close-modal="createEditModalActive = false"
      @save="onSave" />
    <BaseModalConfirm
      v-if="modalDeleteRow"
      @cancel="modalDeleteRow = false"
      @confirm="onDeleteSingle()" />
  </section>
</template>
<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { v4 as uuidv4 } from 'uuid';
import { storeToRefs } from 'pinia';
import { useToastStore } from '@/stores/toast';
import { useLookupsStore } from '@/stores/lookups';
import { useI18n } from 'vue-i18n';
import { getRouteDefinitions, removeRouteDefinitionsByIds } from '@/services/api/routes';
import { filterActive, filterData, sortData } from '@/utils/table';
import RouteDefinitionModalCreateEdit from '@/components/routes/RouteDefinitionModalCreateEdit.vue';
import BaseTable from '@/components/common/BaseTable.vue';
import BaseModalConfirm from '@/components/common/BaseModalConfirm.vue';
import IconTrashOutline from '@/assets/icons/IconTrashOutline.vue';
import ActionButton from '@/components/common/ActionButton.vue';
import BaseToggle from '@/components/common/BaseToggle.vue';

import type { TableFilterData } from '@/types/Base';
import type { RouteDefinitions } from '@/types/Routes';

const { t: $t } = useI18n({ useScope: 'global' });

const {
  getUserTypesLookup,
  getRouteTypesLookup,
  getMwAccountGroupsLookup,
  getSalespersonTypesLookup,
  reloadLookups,
} = useLookupsStore();
const { userTypes, routeTypes, mwAccountGroups, salesPersonTypes } = storeToRefs(useLookupsStore());

const toastStore = useToastStore();

const routeDefinitions = ref<RouteDefinitions[]>([]);
const filterIsActive = ref<boolean | undefined>(false);
const routeDefinitionHeaders = [
  { value: 'name', text: $t('baseObject.name'), type: 'text', active: true },
  { value: 'mwRouteType.name', text: $t('routes.typeOfRoute'), type: 'text', active: true },
  { value: 'mwUserType.name', text: $t('routes.typeOfUser'), type: 'text', active: true },
  {
    value: 'mwAccountGroup.accountGroup',
    text: $t('routes.accountGroup'),
    type: 'text',
    active: true,
  },
  { value: 'isActive', text: $t('baseObject.isActive'), type: 'check', active: true },
  { value: 'actions', text: $t('baseObject.actions'), active: true },
];

onMounted(async () => {
  userTypes.value = [];
  routeTypes.value = [];
  mwAccountGroups.value = [];
  salesPersonTypes.value = [];
  await getUserTypesLookup();
  await getRouteTypesLookup();
  await getMwAccountGroupsLookup();
  await getSalespersonTypesLookup();

  await loadData();
});

const routeDefinition = ref<RouteDefinitions>(initializeRouteDefinition());

const createEditModalActive = ref(false);

const createEditModalMode = ref<'add' | 'edit'>('add');

function initializeRouteDefinition(): RouteDefinitions {
  return {
    id: uuidv4(),
    name: '',
    mwRouteTypeId: '',
    mwUserTypeId: '',
    mwAccountGroupId: '',
    isActive: true,
    isFromManager: true,
  };
}

function onAddNewRouteDefinition() {
  routeDefinition.value = initializeRouteDefinition();
  createEditModalActive.value = true;
  createEditModalMode.value = 'add';
}

function onEditRouteDefinition(item: RouteDefinitions) {
  routeDefinition.value = item;
  createEditModalActive.value = true;
  createEditModalMode.value = 'edit';
}

async function onSave(modifiedData: RouteDefinitions) {
  if (!routeDefinitions.value) return;

  const index = routeDefinitions.value.findIndex((item) => item.id === modifiedData.id);
  if (index !== -1) routeDefinitions.value[index] = modifiedData;
  else routeDefinitions.value.push(modifiedData);
  createEditModalActive.value = false;

  await reloadLookups(['routeDefinitions']);
}

async function loadData(data?: TableFilterData) {
  let response = await getRouteDefinitions();
  if (!response) return;

  if (data && data.query) response = filterData(response, data.query, ['name']);

  if (data && data.column !== undefined && data.order)
    response = sortData(response, data.column, data.order, routeDefinitionHeaders);

  if (data && data.isActive !== undefined) response = filterActive(response, data.isActive);

  routeDefinitions.value = response;
}

async function onDeleteMultiselect(items: RouteDefinitions[]) {
  const idsToDelete = items.filter((item) => item.isFromManager === true).map((item) => item.id);
  if (!idsToDelete.length) {
    toastStore.warning({ text: $t('messages.info.selectedItemsCannotBeDeleted') });
    return;
  }
  const res = await removeRouteDefinitionsByIds(idsToDelete);
  if (!res && routeDefinitions.value)
    routeDefinitions.value = routeDefinitions.value.filter(
      (type) => !idsToDelete.includes(type.id)
    );
  res
    ? toastStore.attention({ text: res })
    : toastStore.success({ text: $t('messages.success.successfullyDeleted') });

  await reloadLookups(['routeDefinitions']);
}

async function onDeleteSingle() {
  modalDeleteRow.value = false;
  if (!deleteRowItem.value) return;
  const idToDelete = deleteRowItem.value.id;
  const res = await removeRouteDefinitionsByIds([idToDelete]);
  if (!res && routeDefinitions.value)
    routeDefinitions.value = routeDefinitions.value.filter((type) => type.id !== idToDelete);
  res
    ? toastStore.error({ text: res })
    : toastStore.success({ text: $t('messages.success.successfullyDeleted') });

  await reloadLookups(['routeDefinitions']);
}

const modalDeleteRow = ref(false);

const deleteRowItem = ref<RouteDefinitions>();
function onDeleteRow(item: RouteDefinitions) {
  deleteRowItem.value = item;
  modalDeleteRow.value = true;
}

function modalEditCreateTitle() {
  if (routeDefinition.value && !routeDefinition.value.isFromManager)
    return `${$t('common.actions.view')}  ${$t('routes.definition')} `;
  return createEditModalMode.value === 'edit'
    ? `${$t('common.actions.edit')} ${$t('routes.definition')}`
    : `${$t('common.actions.create')} ${$t('routes.definition')}`;
}

function onApplyFilters(data: TableFilterData) {
  data.isActive = filterIsActive.value ?? undefined;
  loadData(data);
}

function onResetFilters() {
  filterIsActive.value = undefined;
  loadData({});
}
</script>
